import { QueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

// Define cache times for different types of data
const CACHE_TIMES = {
  // Static data that rarely changes
  STATIC: {
    staleTime: 1000 * 60 * 60, // 1 hour
    cacheTime: 1000 * 60 * 60 * 2 // 2 hours
  },
  // Semi-static data (buildings, rooms)
  SEMI_STATIC: {
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 30 // 30 minutes
  },
  // Dynamic data (contracts, payments)
  DYNAMIC: {
    staleTime: 1000 * 60, // 1 minute
    cacheTime: 1000 * 60 * 10 // 10 minutes
  },
  // Real-time data (notifications, tickets)
  REAL_TIME: {
    staleTime: 0, // Always fresh
    cacheTime: 1000 * 60 * 5 // 5 minutes
  }
};

// Create query client with optimized defaults
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // General defaults
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Default cache settings (can be overridden per query)
      staleTime: CACHE_TIMES.DYNAMIC.staleTime,
      cacheTime: CACHE_TIMES.DYNAMIC.cacheTime,

      // Error handling
      onError: (error) => {
        const message = error?.response?.data?.detail || error?.message || 'An error occurred';

        // Handle auth errors
        if (error?.response?.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return;
        }

        // Show error toast for other errors
        if (typeof toast !== 'undefined' && toast.error) {
          toast.error(message);
        }
      }
    },
    mutations: {
      // Mutation defaults
      retry: false,
      onError: (error) => {
        const message = error?.response?.data?.detail || error?.message || 'Operation failed';

        // Handle auth errors
        if (error?.response?.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return;
        }

        // Show error toast
        if (typeof toast !== 'undefined' && toast.error) {
          toast.error(message);
        }
      },
      onSuccess: () => {
        // Default success behavior can be overridden
      }
    }
  }
});

// Query key factory for consistent key generation
export const queryKeys = {
  // Auth
  auth: {
    user: () => ['auth', 'user'],
    permissions: () => ['auth', 'permissions']
  },

  // Users
  users: {
    all: () => ['users'],
    list: (filters) => ['users', 'list', filters],
    detail: (id) => ['users', 'detail', id],
    renters: () => ['users', 'renters']
  },

  // Properties
  properties: {
    buildings: {
      all: () => ['properties', 'buildings'],
      detail: (id) => ['properties', 'buildings', id],
      rooms: (buildingId) => ['properties', 'buildings', buildingId, 'rooms']
    },
    rooms: {
      all: () => ['properties', 'rooms'],
      detail: (id) => ['properties', 'rooms', id],
      furniture: (roomId) => ['properties', 'rooms', roomId, 'furniture'],
      history: (roomId) => ['properties', 'rooms', roomId, 'history'],
      financial: (roomId) => ['properties', 'rooms', roomId, 'financial']
    },
    analytics: {
      revenue: () => ['properties', 'analytics', 'revenue'],
      occupancy: () => ['properties', 'analytics', 'occupancy'],
      stats: () => ['properties', 'stats']
    }
  },

  // Contracts
  contracts: {
    all: () => ['contracts'],
    detail: (id) => ['contracts', id],
    templates: () => ['contracts', 'templates'],
    byRoom: (roomId) => ['contracts', 'room', roomId],
    byTenant: (tenantId) => ['contracts', 'tenant', tenantId]
  },

  // Finance
  finance: {
    payments: {
      all: () => ['finance', 'payments'],
      detail: (id) => ['finance', 'payments', id],
      history: (id) => ['finance', 'payments', id, 'history']
    },
    invoices: () => ['finance', 'invoices'],
    reports: () => ['finance', 'reports']
  },

  // Tickets
  tickets: {
    all: () => ['tickets'],
    detail: (id) => ['tickets', id],
    byStatus: (status) => ['tickets', 'status', status],
    byRoom: (roomId) => ['tickets', 'room', roomId]
  },

  // Notifications
  notifications: {
    all: () => ['notifications'],
    unread: () => ['notifications', 'unread'],
    preferences: () => ['notifications', 'preferences']
  },

  // Dashboard
  dashboard: {
    widgets: () => ['dashboard', 'widgets'],
    stats: () => ['dashboard', 'stats']
  },

  // Violations
  violations: {
    all: () => ['violations'],
    detail: (uuid) => ['violations', 'detail', uuid],
    byStatus: (status) => ['violations', 'status', status],
    bySeverity: (severity) => ['violations', 'severity', severity],
    evidence: (violationUuid) => ['violations', violationUuid, 'evidence'],
    comments: (violationUuid) => ['violations', violationUuid, 'comments']
  }
};

// Prefetch common data
export const prefetchCommonData = async () => {
  // These can be imported from your API service
  const { propertyService, userService } = await import('../services/apiService');

  // Prefetch buildings and rooms (semi-static data)
  await queryClient.prefetchQuery({
    queryKey: queryKeys.properties.buildings.all(),
    queryFn: () => propertyService.getBuildings(),
    ...CACHE_TIMES.SEMI_STATIC
  });

  // Prefetch user data
  await queryClient.prefetchQuery({
    queryKey: queryKeys.auth.user(),
    queryFn: () => userService.getCurrentUser(),
    ...CACHE_TIMES.STATIC
  });
};

// Invalidation patterns for related data
export const invalidatePatterns = {
  // When a contract is created/updated
  contractChange: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all() });
    queryClient.invalidateQueries({ queryKey: queryKeys.properties.rooms.all() });
    queryClient.invalidateQueries({ queryKey: queryKeys.users.renters() });
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats() });
  },

  // When a payment is made
  paymentChange: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.finance.payments.all() });
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats() });
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.widgets() });
  },

  // When a ticket is created/updated
  ticketChange: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all() });
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.widgets() });
  },

  // When a room status changes
  roomStatusChange: (roomId) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.properties.rooms.detail(roomId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.properties.rooms.all() });
    queryClient.invalidateQueries({ queryKey: queryKeys.properties.analytics.occupancy() });
  },

  // When user data changes
  userChange: (userId) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    if (userId === 'current') {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.user() });
    }
  },

  // When a violation is created/updated
  violationChange: (violationUuid) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.violations.all() });
    if (violationUuid) {
      queryClient.invalidateQueries({ queryKey: queryKeys.violations.detail(violationUuid) });
      queryClient.invalidateQueries({ queryKey: queryKeys.violations.evidence(violationUuid) });
      queryClient.invalidateQueries({ queryKey: queryKeys.violations.comments(violationUuid) });
    }
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.widgets() });
  }
};

// Optimistic update helper
export const optimisticUpdate = (queryKey, updateFn) => {
  return {
    onMutate: async (newData) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => updateFn(old, newData));
      return { previousData };
    },
    onError: (err, newData, context) => {
      queryClient.setQueryData(queryKey, context.previousData);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  };
};

// Batch invalidation helper
export const batchInvalidate = (queryKeyPatterns) => {
  queryKeyPatterns.forEach(pattern => {
    queryClient.invalidateQueries({ queryKey: pattern });
  });
};

// Clear all cache (useful for logout)
export const clearAllCache = () => {
  queryClient.clear();
};

// Export cache times for use in components
export { CACHE_TIMES };

export default queryClient;